import { useCallback, useEffect, useMemo } from 'react';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkReturnTicketDialog } from 'src/dialogs/BulkEdits/BulkReturnTicketDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_RETURN_TICKET_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchBulkReturnTicket } from './LaunchBulkReturnTicket';

export const useBulkReturnTicketAction = (
  filterQuery: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `bulkReturnTicket-event-${event.virtId}`
    : INVENTORY_BULK_RETURN_TICKET_KEY;
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkReturnTicketDialog,
    closeDialog: closeBulkReturnTicketDialog,
  } = useDialog(DialogId.BulkReturnTicket, BulkReturnTicketDialog);

  const onSubmit = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkReturnTicketPreview(filterQuery);
            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkReturnTicketPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkReturnTicket(
              preview!.preview,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }
              closeBulkReturnTicketDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitReturnTicket', error, {
              trackErrorData: {
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQuery,
      showErrorDialog,
      updateKey,
      closeBulkReturnTicketDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onLaunchAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkReturnTicketDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkReturnTicketDialog,
      });
    },
    [
      closeBulkReturnTicketDialog,
      onSubmit,
      openBulkReturnTicketDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkReturnTicketDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkReturnTicketDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkReturnTicket,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkReturnTicket,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        disabled: isLoading,
        onClick: onLaunchAction,
        title: ContentId.ReturnTickets,
      },
    ];
  }, [isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchBulkReturnTicket
        key="bulkReturnTicket"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
